import { withSearch } from "@elastic/react-search-ui";
import type { SearchResult } from "@elastic/search-ui";
import { useStoreContext } from "context/StoreProvider";
import { Col } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  chapterString,
  codesystems,
  collectionsString,
  getRaw,
  paths
} from "utils";

import { handleNavigate } from "../../../../../utils";
import AdmResultView from "./AdmResultView";
import ChapterResultView from "./ChapterResultView";
import {
  ResultViewWrapper,
  SearchResultBar,
  TableHeader,
  TableHeaderContainer
} from "./style";

const ResultView = ({ results, searchTerm, addFilter }: SearchResult) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { actions, state } = useStoreContext();
  const { codeSystem, excludedSearchFields } = state;
  const { updateCodeValue } = actions;
  const { pathname } = useLocation();
  const isAdm = pathname.startsWith(paths.adm);
  const isNorpat = pathname.startsWith(paths.norpat);
  const headerHeight =
    document.querySelector("#site-header")?.clientHeight || 0;
  const tabNavHeight =
    document.querySelector("#codesystem-tab-nav")?.clientHeight || 0;
  const searchWrapperHeight =
    document.querySelector("#search-wrapper")?.clientHeight || 0;
  const tabViewHeight =
    document.querySelector(`#tab-view-${codeSystem}`)?.clientHeight || 0;
  const metaBarHeight =
    document.querySelector("#result-meta-bar")?.clientHeight || 0;

  const calculateHeightClinical =
    headerHeight +
    tabNavHeight +
    searchWrapperHeight +
    tabViewHeight +
    metaBarHeight +
    4;

  const calculateHeightMobile =
    headerHeight +
    tabNavHeight +
    searchWrapperHeight +
    metaBarHeight +
    tabViewHeight +
    4;

  //TODO: clean up this retarded code

  const isMobileCondition = isMobile || window.innerWidth < 992;

  const getTopValue = () => {
    if (isAdm) {
      return calculateHeightClinical - 2;
    }
    if (isMobileCondition) {
      return calculateHeightMobile;
    }
    if (isNorpat) {
      return calculateHeightClinical - 1;
    }
    return calculateHeightClinical;
  };

  return (
    <SearchResultBar $isAdm={isAdm} role="table" className="position-relative">
      <TableHeaderContainer
        $topValue={`${getTopValue()}`}
        role="rowgroup"
        $isAdm={isAdm}
      >
        <TableHeader role="row" className="gap-1 gap-md-3 flex-nowrap">
          {isAdm ? (
            <>
              <Col role="columnheader" className="p-0" xs={1}>
                <span>{t("general.id")}</span>
              </Col>
              <Col role="columnheader" xs={3} className="p-0">
                <span>{t("general.codesystem")}</span>
              </Col>
              <Col role="columnheader" xs={2} className="p-0">
                <span>{t("search.admTable.owner")}</span>
              </Col>
              <Col role="columnheader" xs={3} md={2} className="p-0">
                <span>{t("search.admTable.collection")}</span>
              </Col>
              <Col role="columnheader" xs={2} className="p-0">
                <span>{t("search.sorting.category")}</span>
              </Col>
              <Col role="columnheader" xs={1} className="text-end p-lg-0">
                <span>{t("search.admTable.status")}</span>
              </Col>
            </>
          ) : (
            <>
              <Col role="columnheader" xs="auto" className="p-0">
                <span>{t("general.codeValue")}</span>
              </Col>
              <Col role="columnheader" xs="auto" className="p-0">
                <span>{t("general.codeText")}</span>
              </Col>
            </>
          )}
        </TableHeader>
      </TableHeaderContainer>
      {results.map((result: SearchResult) => {
        const shouldBeDisabled =
          result.active.raw === "false" &&
          result.code_system.raw !== codesystems.adm &&
          result.code_system.raw !== codesystems.norpat;

        const handleClickedResult = () => {
          if (result.code_system.raw === codesystems.adm) {
            navigate(
              `${handleNavigate(result.code_system.raw)}/${collectionsString}/${
                result.compact_code.raw.split(" ")[0]
              }`
            );
            updateCodeValue(result.compact_code.raw.split(" ")[0]);
          } else if (result.code_system.raw === codesystems.icpc2b) {
            navigate(
              `${handleNavigate(result.code_system.raw)}/${chapterString}/${
                result.parent_code.raw
              }`
            );
            updateCodeValue(result.parent_code.raw);
          } else {
            if (shouldBeDisabled) {
              return null;
            }

            navigate(
              `${handleNavigate(
                result.code_system.raw
              )}/${chapterString}/${result.code.raw.replace("_", "")}`
            );
            updateCodeValue(result.code.raw);
          }
        };

        return (
          <ResultViewWrapper
            $isDisabled={shouldBeDisabled}
            onClick={handleClickedResult}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === "Enter" || event.key === " ") {
                handleClickedResult();
              }
            }}
            action={!shouldBeDisabled}
            key={
              result.code_system.raw === codesystems.adm
                ? getRaw(result, "compact_code")?.slice(0, 4)
                : getRaw(result, "code")
            }
            role="rowgroup"
            className="w-100"
          >
            {getRaw(result, "code_system") === codesystems.adm ? (
              <AdmResultView
                result={result}
                searchTerm={searchTerm}
                showCodes={isAdm && !excludedSearchFields.includes("code")}
              />
            ) : (
              <ChapterResultView
                result={result}
                searchTerm={searchTerm}
                addFilter={addFilter}
              />
            )}
          </ResultViewWrapper>
        );
      })}
    </SearchResultBar>
  );
};

export default withSearch(({ searchTerm, results, addFilter }) => ({
  searchTerm,
  results,
  addFilter
}))(ResultView);
